import React, { useEffect, useState } from "react";
import { sendClientSideSplitTrackEvent } from "@leafly-com/split-next";
import axios from "axios";
import { useSelector } from "react-redux";

import getFeaturedDispensaries from "api/requests/consumerApi/getFeaturedDispensaries";
import { useEventTracker } from "hooks/useEventTracker";
import { getDomainCountryCode } from "redux/selectors/config";
import { getCoordinates } from "redux/selectors/location";
import { getUserId } from "redux/selectors/user";
import { determineOrderMethod } from "utils/determineOrderMethod";
import { getRoundedDistance } from "utils/distance";
import { localizeText } from "utils/localizeText";
import { getSplitKey } from "utils/split/getSplitKey";
import { Action, Category } from "utils/trackEvent";

import Carousel from "components/botanic/Carousel";
import DispensaryCardPlaceholder from "components/botanic/DispensaryCard/DispensaryCardPlaceholder";
import PickupReadinessDispensaryCard from "components/PickupReadinessDispensaryCard";
import TrackImpression from "components/TrackImpression";

const DISPENSARY_CARD_VARIANT = "platinum";

type Props = {
  className?: string;
};

const FeaturedDispensariesSection = ({ className = "" }: Props) => {
  const [featuredDispensaries, setFeaturedDispensaries] = useState([
    {},
    {},
    {},
    {},
  ]);
  const { trackEvent, trackNonInteractionEvent } = useEventTracker();

  const splitKey = getSplitKey();

  const countryCode = useSelector(getDomainCountryCode);
  const coordinates = useSelector(getCoordinates);
  const userId = useSelector(getUserId);

  useEffect(() => {
    if (coordinates?.lat && coordinates?.lon) {
      getFeaturedDispensaries(coordinates, countryCode).then((dispensaries) =>
        setFeaturedDispensaries(dispensaries)
      );
    } else {
      setFeaturedDispensaries([]);
    }
  }, []);
  return featuredDispensaries?.length ? (
    <>
      <TrackImpression
        category="Featured Dispensaries Carousel"
        label="Featured Dispensaries Carousel"
        data-testid="featured-dispensaries-section"
        className={className}
      >
        <div className="container">
          <Carousel
            title="Local dispensaries"
            subtitle={`Order online from our sponsored ${
              countryCode === "US" ? "partners." : "stores."
            }`}
            seeMoreText={`See all ${localizeText("dispensaries", countryCode)}`}
            seeMoreHref="/dispensaries"
            showSeeMoreCard={true}
            seeMoreOnClick={() => {
              trackEvent(
                Category.Carousel,
                Action.click,
                "home featured dispensaries carousel show all"
              );
            }}
            seeMoreCardClass="rounded overflow-hidden elevation-low"
          >
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate */}
            {featuredDispensaries.map((dispensary: any, slot) => {
              if (dispensary.name) {
                const deliveryEnabled =
                  !!dispensary.deliveryEnabled ||
                  !!dispensary.hasDeliveryEnabled;
                const pickupEnabled = !!dispensary.pickupEnabled;

                const eventLabel = determineOrderMethod(
                  pickupEnabled,
                  deliveryEnabled,
                  dispensary.featureTier,
                  dispensary.tags
                );

                const eventOnClick = () => {
                  trackEvent(
                    Category.homepageFeature,
                    Action.click,
                    `Slot #${slot + 1}: ${eventLabel}`,
                    {
                      deal_id: dispensary.featuredDeal?.id,
                      dispensaryDistance: getRoundedDistance(
                        dispensary.distanceMi,
                        countryCode
                      ),
                      dispensaryId: dispensary.id,
                      merchandisingCampaignId:
                        dispensary.merchandisingCampaignId,
                      userId: userId as number,
                    }
                  );
                  sendClientSideSplitTrackEvent(
                    "WH_Homepage_featdispensariescardClick",
                    splitKey
                  );
                };

                const impressionLabel = `Slot #${slot + 1}: ${eventLabel}`;

                return (
                  <TrackImpression
                    category={Category.homepageFeature}
                    className="h-full w-full relative"
                    key={`featured-dispensary-${slot}`}
                    label={impressionLabel}
                    trackingFunction={() => {
                      trackNonInteractionEvent(
                        Category.homepageFeature,
                        Action.impression,
                        impressionLabel,
                        {
                          deal_id: dispensary.featuredDeal?.id,
                          dispensaryDistance: getRoundedDistance(
                            dispensary.distanceMi,
                            countryCode
                          ),
                          dispensaryId: dispensary.id,
                          merchandisingCampaignId:
                            dispensary.merchandisingCampaignId,
                          userId: userId as number,
                        }
                      );
                      sendClientSideSplitTrackEvent(
                        "WH_Homepage_featdispensariesImpression",
                        splitKey
                      );
                      // this is for kevel impression tracking, ask kayleigh
                      if (dispensary.impressionUrl) {
                        axios.get(dispensary.impressionUrl);
                      }
                    }}
                  >
                    <PickupReadinessDispensaryCard
                      coverPhotoSizes={[196, null, null, 207, null, 207]}
                      currentStatus={dispensary.currentStatuses?.store}
                      deliveryEnabled={deliveryEnabled}
                      disableLazyLoad={true}
                      preserveHeight={true}
                      deal={dispensary.featuredDeal}
                      dispensaryId={dispensary.id}
                      distanceMi={dispensary.distanceMi}
                      href={dispensary.href}
                      logoUrl={dispensary.logoUrl}
                      name={dispensary.name}
                      onClick={eventOnClick}
                      pickupEnabled={pickupEnabled}
                      photoUrl={dispensary.coverPhotoUrl}
                      preorderConfigured={dispensary.preorderConfigured}
                      rating={dispensary.rating}
                      reviewCount={dispensary.reviewCount}
                      scheduleType="store"
                      slug={dispensary.slug}
                      tags={dispensary.tags}
                      timeZone={dispensary.timeZone}
                      variant={DISPENSARY_CARD_VARIANT}
                      data-testid="featured-dispensaries-section-card"
                    />
                  </TrackImpression>
                );
              } else {
                return (
                  <DispensaryCardPlaceholder
                    key={slot}
                    variant={DISPENSARY_CARD_VARIANT}
                    id={`featured-dispensary-card-placeholder-${slot}`}
                  />
                );
              }
            })}
          </Carousel>
        </div>
      </TrackImpression>
    </>
  ) : null;
};

export default FeaturedDispensariesSection;
